// Shared line-art icon set. The dashboard header nav and the project rail used to
// render EMOJI (📁 📖 📊 ✓ 🖥️ 🐞 🔍 «), which was a dead end for two reasons: emoji are
// drawn by the platform's own colour font, so CSS `color` cannot touch them, and each
// vendor's art has its own weight and perspective — six of them in a row read as
// six unrelated pictures rather than one toolbar.
//
// These replace them. There is deliberately NO icon dependency (no lucide/heroicons/
// react-icons): eight glyphs is not worth a runtime dep plus bundle weight, and
// hand-authoring is what lets every icon share ONE geometry contract:
//
//   * 24×24 viewBox, artwork inset to roughly x/y 4..20
//   * stroke, never fill, at strokeWidth 1.75 with round caps + joins
//   * sized in `em`, so the call site's existing `font-size` stays the size knob
//     (`.dash-nav-icon { font-size: 24px }` keeps working untouched)
//   * `stroke="currentColor"`, so colour stays a CSS concern per call site
//
// That shared grid and stroke weight IS the design — it is what makes a folder, a
// beetle and a database cylinder look like members of one set. **Any icon added here
// must follow all four rules**, or it will read as pasted in from somewhere else.
//
// `aria-hidden` + `focusable="false"` on every icon: each of these sits inside a
// button that already carries its own `title`/`aria-label`, which is what the tests
// query by. A focusable or name-contributing SVG would both pollute those accessible
// names and add phantom tab stops.
//
// Colour lives at the call site, not here — see `.dash-nav-icon` (Dashboard.css) for
// the header toolbar's brand purple `#7c3aed`.

// The one wrapper every icon shares, so the geometry contract above is stated once
// instead of being re-typed (and drifted) eight times.
function Icon({ className, children }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

// New project — the rail's own action. A plain plus rather than a document glyph:
// what the button does is start a fresh conversation in the centre pane, and the
// project it creates does not exist until something is typed into the composer.
export function PlusIcon({ className }) {
  return (
    <Icon className={className}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Icon>
  );
}

// Build it myself — the rail's second action, which creates an empty project and
// opens the workflow editor on it. Three connected modules rather than a generic
// grid or plus: what distinguishes this path from New project is not that it makes
// a project, it is that YOU wire the pipeline up instead of describing it to Dennis,
// and the canvas is a node graph. The fan (two into one) is the shape of the
// simplest real pipeline, e.g. Mesh + BC → Solver.
export function NodesIcon({ className }) {
  return (
    <Icon className={className}>
      <rect x="4" y="5" width="6" height="5" rx="1.5" />
      <rect x="4" y="14" width="6" height="5" rx="1.5" />
      <rect x="14" y="9.5" width="6" height="5" rx="1.5" />
      {/* Both left outputs and the trunk joining them, in one stroke: right, down,
          left. Then the stub into the right module at its vertical centre (y 12). */}
      <path d="M10 7.5h2v9h-2" />
      <path d="M12 12h2" />
    </Icon>
  );
}

// Files — the repository picker.
export function FolderIcon({ className }) {
  return (
    <Icon className={className}>
      <path d="M3 7a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.6.8L12 7.8h7a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </Icon>
  );
}

// Learn — an open book, matching the 📖 it replaces. The centre spine is a separate
// stroke so the two page curves stay symmetric about it.
export function BookIcon({ className }) {
  return (
    <Icon className={className}>
      <path d="M12 6.8V19.2" />
      <path d="M12 6.8C10.5 5.6 8.7 5 6.8 5H4.8a.8.8 0 0 0-.8.8v10.6a.8.8 0 0 0 .8.8h2c1.9 0 3.7.6 5.2 1.8" />
      <path d="M12 6.8c1.5-1.2 3.3-1.8 5.2-1.8h2a.8.8 0 0 1 .8.8v10.6a.8.8 0 0 1-.8.8h-2c-1.9 0-3.7.6-5.2 1.8" />
    </Icon>
  );
}

// Databases — the stacked-cylinder database glyph. Note this does NOT mirror the 📊
// bar chart it replaces: that emoji was itself the drift (CLAUDE.md documented this
// nav item as "🗄 Databases"), and the destination is /databases, not an analytics view.
export function DatabaseIcon({ className }) {
  return (
    <Icon className={className}>
      <ellipse cx="12" cy="6.3" rx="7" ry="2.8" />
      <path d="M19 6.3v5.7c0 1.55-3.13 2.8-7 2.8s-7-1.25-7-2.8V6.3" />
      <path d="M19 12v5.7c0 1.55-3.13 2.8-7 2.8s-7-1.25-7-2.8V12" />
    </Icon>
  );
}

// Quality Check — the ✓ it replaces, ringed so it reads as a verdict rather than as
// a checkbox the user is meant to tick.
export function CheckCircleIcon({ className }) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.2 12.3l2.6 2.6 5-5.4" />
    </Icon>
  );
}

// Process Monitor — a display on a stand.
export function MonitorIcon({ className }) {
  return (
    <Icon className={className}>
      <rect x="3" y="4.5" width="18" height="12.5" rx="2" />
      <path d="M12 17v3.5" />
      <path d="M8.5 20.5h7" />
    </Icon>
  );
}

// Report bug — a beetle, matching the 🐞. The wing split is load-bearing: without it
// the body reads as an undifferentiated blob at 16px.
export function BugIcon({ className }) {
  return (
    <Icon className={className}>
      <path d="M12 7.5c3 0 5.2 2.4 5.2 5.6v1.6c0 3.1-2.3 5.6-5.2 5.6s-5.2-2.5-5.2-5.6v-1.6c0-3.2 2.2-5.6 5.2-5.6Z" />
      <path d="M12 11.5V20" />
      <path d="M9.6 8.3 8.2 5.6" />
      <path d="M14.4 8.3l1.4-2.7" />
      <path d="M6.8 12.2H4.1" />
      <path d="M6.8 16.4H4.6" />
      <path d="M17.2 12.2h2.7" />
      <path d="M17.2 16.4h2.2" />
    </Icon>
  );
}

// Rail search (⌘K).
export function SearchIcon({ className }) {
  return (
    <Icon className={className}>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.5 15.5 4.5 4.5" />
    </Icon>
  );
}

// A subagent is working. An open arc rather than a ring, because the GAP is what
// makes the rotation legible — a full circle spinning looks static. Rotated by
// `@keyframes dash-activity-spin` in Dashboard.css, which also drops the animation
// under `prefers-reduced-motion` (the precedent `TileStatusDot.css` sets for its
// pulsing dot).
export function SpinnerIcon({ className }) {
  return (
    <Icon className={className}>
      <path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5" />
    </Icon>
  );
}

// A planned step that has not started. A DASHED ring, not a solid one: solid reads
// as a state the module is in, dashed as one it has not reached — the same
// distinction `TileStatusDot` draws between its filled and hollow dots.
export function CircleDashedIcon({ className }) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.5" strokeDasharray="3 3" />
    </Icon>
  );
}

// A module is waiting on the user. Matches `TileStatusDot`'s own choice to render
// this state as a glyph rather than as art, so the two read as one state.
export function AlertCircleIcon({ className }) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8v4.5" />
      <path d="M12 15.6v.1" />
    </Icon>
  );
}

// Something failed. A triangle, not a ringed glyph: the silhouette alone
// distinguishes it from `AlertCircleIcon` at 16px, which matters because amber
// "needs you" and red "failed" would otherwise differ only by colour.
export function AlertTriangleIcon({ className }) {
  return (
    <Icon className={className}>
      <path d="M12 4.8 20.2 19H3.8L12 4.8Z" />
      <path d="M12 10v3.6" />
      <path d="M12 16.4v.1" />
    </Icon>
  );
}

// Stopped before it finished — a cancelled run and every module of it that had not
// already reported. A ringed SLASH rather than a cross: the ring keeps it in the same
// family as `CircleDashedIcon`/`AlertCircleIcon` (the other "state of one module"
// glyphs), while the slash reads as "struck out" instead of "went wrong", which is the
// distinction from `AlertTriangleIcon`. Cancelling is a decision the user made; it is
// not a failure, and it is emphatically not the ✓ that used to be drawn here.
export function CircleSlashIcon({ className }) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.4 15.6 15.6 8.4" />
    </Icon>
  );
}

// The run is HELD. Two bars inside the same ring as the glyphs above, because pause is
// a state of the run rather than an action here — the button that performs it lives in
// the thread header and carries its own label.
export function CirclePauseIcon({ className }) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M10.2 9.3v5.4" />
      <path d="M13.8 9.3v5.4" />
    </Icon>
  );
}

// The rail's collapse/expand control. DOUBLE chevron on purpose — it replaces « / »,
// and a single chevron would read as "previous/next" rather than "collapse this rail".
export function ChevronsIcon({ className, direction = 'left' }) {
  return (
    <Icon className={className}>
      {direction === 'left' ? (
        <>
          <path d="m12.5 6.5-5.5 5.5 5.5 5.5" />
          <path d="m19 6.5-5.5 5.5 5.5 5.5" />
        </>
      ) : (
        <>
          <path d="m11.5 6.5 5.5 5.5-5.5 5.5" />
          <path d="m5 6.5 5.5 5.5-5.5 5.5" />
        </>
      )}
    </Icon>
  );
}
